/** @jsx jsx */
import { useState, useEffect, useRef } from 'react'
import validator from 'validator'
import { css, jsx, keyframes } from '@emotion/core'
import { Input, Select, Switch } from '@livechat/design-system'

import textIcon from '../../assets/icons/text.svg'
import singleChoiceIcon from '../../assets/icons/single-choice.svg'
import multiChoiceIcon from '../../assets/icons/multi-choice.svg'
import scoreIcon from '../../assets/icons/score.svg'
import { TYPES } from '../../constants'

import ManagePanel from './ManagePanel'
import Answers from './Answers'
import Score from './Score'

const { TEXT_ANSWER, SINGLE_CHOICE, MULTI_CHOICE, SCORE } = TYPES

const icons = {
  'Text answer': textIcon,
  'Single choice': singleChoiceIcon,
  'Multi choice': multiChoiceIcon,
  Score: scoreIcon,
}

const types = [
  { key: TEXT_ANSWER, props: { name: 'Text answer', value: TEXT_ANSWER } },
  {
    key: SINGLE_CHOICE,
    props: { name: 'Single choice', value: SINGLE_CHOICE },
  },
  { key: MULTI_CHOICE, props: { name: 'Multi choice', value: MULTI_CHOICE } },
  { key: SCORE, props: { name: 'Score', value: SCORE } },
]

const showAnimation = keyframes`
 from {
  transform: scale(0.95);
 }
`

const fieldCss = (isTextAnswer, shine) => css`
  width: 600px;
  display: flex;
  background-color: white;
  border-radius: 4px;
  box-shadow: rgb(228 228 228) 0px 1px 5px 0px;

  @media (max-width: 640px) {
    width: 94vw;
  }

  ${shine &&
  css`
    animation: ${showAnimation} 0.2s ease;
  `};

  .inputs-panel {
    display: flex;
    flex-direction: column;
    width: calc(100% - 90px);
    padding: 20px;
    ${isTextAnswer &&
    css`
      padding-bottom: 12px;
    `};

    @media (max-width: 640px) {
      padding-bottom: 20px;
    }
  }

  .error {
    color: #d64646;
    font-size: 14px;
    margin-top: 3px;
    font-weight: 100;
  }

  .type {
    width: 214px;
  }

  .top {
    display: flex;

    @media (max-width: 640px) {
      flex-direction: column;
    }
  }

  .bottom {
    display: flex;
    justify-content: space-between;
    margin-top: 3px;
  }

  .bottom-right {
    display: flex;

    @media (max-width: 640px) {
      display: none;
    }

    > .label {
      color: #6b6d6f;
      font-weight: 300;
      font-size: 14px;
      line-height: 22px;
      margin-top: 2px;
    }
  }

  .bottom-left {
    flex-direction: column;

    @media (max-width: 640px) {
      width: 100%;
    }

    .answers {
      display: flex;
      flex-direction: column;

      > div {
        margin: 5px 0;
      }
    }

    .lc-input {
      width: 257px;
      margin-right: 8px;

      @media (max-width: 640px) {
        width: 100%;
      }
    }
    .lc-btn {
      border-color: #bcc6d0;
      padding: 5px;
    }
  }
`

const lcCss = css`
  .lc-input {
    height: 36px;
    width: 320px;

    @media (max-width: 640px) {
      width: 100%;
    }
  }

  .lc-select {
    height: 36px;
    width: 214px;
    margin-left: 8px;

    @media (max-width: 640px) {
      margin-left: 0;
      margin-top: 8px;
      width: 100%;
    }
  }

  .lc-select-head {
    height: 20px;
  }

  .lc-switch {
    margin-left: 5px;
    margin-top: 5px;
  }

  .lc-select-body {
    padding: 0;
  }

  .lc-switch__track {
    box-shadow: none !important;
    outline: none !important;
  }

  .lc-select-head__clear {
    opacity: 0;
    pointer-events: none;
  }

  .lc-checkbox__text {
    max-width: 280px;
    word-wrap: break-word;
  }

  .lc-radio__text {
    max-width: 280px;
    word-wrap: break-word;
  }
`

const itemCss = css`
  display: flex;
  align-items: center;

  > span {
    margin-left: 5px;
  }

  > img {
    width: 16px;
  }
`

const Field = ({
  id,
  type,
  question,
  answers,
  range,
  required,
  drop,
  copy,
  update,
  shine,
  disable,
  submitted,
  dragHandleProps,
  index,
  isTemplate,
}) => {
  const [editAnswers, setEditAnswers] = useState([])
  const inputRef = useRef(null)

  useEffect(() => {
    inputRef.current.focus()
  }, [])

  const getItemBody = (props) => {
    if (!props) {
      return null
    }
    return (
      <div
        id={props.value}
        css={itemCss}
        data-cy={`field-${index}-select-item`}
      >
        <img src={icons[props.name]} alt={props.name} />
        <span>{props.name}</span>
      </div>
    )
  }

  const getSelectedItemBody = (props) => {
    return (
      <div id={props.value} css={itemCss}>
        <img src={icons[props.name]} alt={props.name} />
        <span>{props.name}</span>
      </div>
    )
  }

  const isTextAnswer = type ? type === TEXT_ANSWER : true

  const questionError = submitted
    ? question && !validator.isEmpty(question, { ignore_whitespace: true })
      ? question.length > 500
        ? 'Question is too long.'
        : undefined
      : 'Please enter an question.'
    : undefined

  const typeError = submitted
    ? type
      ? undefined
      : 'Please select an type.'
    : undefined

  return (
    <div css={[fieldCss(isTextAnswer, shine), lcCss]} key={id}>
      <div className="inputs-panel">
        <div className="top">
          <div>
            <Input
              ref={inputRef}
              value={question}
              error={questionError}
              id={`input-field-question-${id}`}
              placeholder="Ask your customers an question"
              onChange={(e) => update('question', e.target.value)}
              data-cy={`field-${index}-question`}
            />
            {questionError && <div className="error">{questionError}</div>}
          </div>

          <Select
            id={`field-${index}-select`}
            items={types}
            onItemSelect={(type) => {
              update('type', type)
            }}
            getItemBody={getItemBody}
            placeholder="Select type"
            getSelectedItemBody={getSelectedItemBody}
            selected={type}
            error={typeError}
          />
        </div>
        <div className="bottom">
          <div className="bottom-left">
            {[SINGLE_CHOICE, MULTI_CHOICE].includes(type) && (
              <Answers
                id={id}
                answers={answers}
                type={type}
                editAnswers={editAnswers}
                update={update}
                setEditAnswers={setEditAnswers}
                isTemplate={isTemplate}
                submitted={submitted}
                index={index}
              />
            )}
            {type === SCORE && (
              <Score update={update} submitted={submitted} index={index} />
            )}
          </div>
          <div className="bottom-right">
            <span className="label">
              Answer {required ? 'required' : 'not required'}
            </span>
            <Switch
              on={required}
              size="compact"
              onChange={() => update('required', !required)}
            />
          </div>
        </div>
      </div>
      <ManagePanel
        drop={drop}
        copy={copy}
        type={type}
        anyAnswers={answers && answers.length > 0}
        disable={disable}
        dragHandleProps={dragHandleProps}
      />
    </div>
  )
}

export default Field
